import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { UserCircle, X, Search } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Salesperson selector for the till.
 * Two ways to pick:
 *   1. Type the marketing number into the inline input → fetches by code on Enter.
 *   2. Click "Pick" → modal listing all active salespeople as big buttons.
 *
 * Selection is one-per-sale. Cashier may clear back to "Counter Sale".
 *
 * Props:
 *   value: {id, name, marketing_number} | null
 *   onChange: (next | null) => void
 */
export default function SalespersonSelector({ value, onChange }) {
  const [code, setCode] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("");
  const inputRef = useRef(null);

  // Load salespeople list once for the picker
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/employees/salespeople`);
        setList(res.data || []);
      } catch (e) { /* silent — picker may be empty */ }
    })();
  }, []);

  const lookupByCode = async (rawCode) => {
    const c = (rawCode || "").trim();
    if (!c) return;
    try {
      const res = await axios.get(`${API}/employees/by-marketing-number/${encodeURIComponent(c)}`);
      onChange(res.data);
      setCode("");
      toast.success(`Salesperson: ${res.data.name}`);
    } catch (e) {
      toast.error(e?.response?.status === 404 ? `No active salesperson with code "${c}"` : "Lookup failed");
    }
  };

  const filtered = list.filter((s) =>
    !filter ||
    (s.name || "").toLowerCase().includes(filter.toLowerCase()) ||
    (s.marketing_number || "").toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div
      className={`flex items-center gap-2 px-4 py-1 border-b flex-shrink-0 z-10 ${
        value
          ? "bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300"
          : "bg-slate-50 border-slate-200"
      }`}
      data-testid="salesperson-bar"
    >
      <UserCircle className={`h-5 w-5 ${value ? "text-amber-700" : "text-slate-400"}`} />
      {value ? (
        <>
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-bold text-slate-900" data-testid="active-salesperson-name">
              {value.name}
            </span>
            <span className="text-[11px] text-slate-600 font-mono">
              #{value.marketing_number || "—"}{value.position ? ` · ${value.position}` : ""}
            </span>
          </div>
          <span className="ml-2 px-2 py-0.5 rounded-full bg-amber-600 text-white text-xs font-bold shadow-sm">
            ⭐ Earning Commission
          </span>
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setPickerOpen(true)} className="h-8 text-xs" data-testid="change-salesperson-btn">
              Change
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onChange(null)} className="h-8 text-xs text-red-600 hover:bg-red-50" data-testid="clear-salesperson-btn">
              <X className="h-3.5 w-3.5 mr-1" /> Counter Sale
            </Button>
          </div>
        </>
      ) : (
        <>
          <span className="text-sm font-semibold text-slate-700">Counter Sale</span>
          <span className="text-xs text-slate-500">— enter a marketing # or pick a salesperson to give them credit</span>
          <Input
            ref={inputRef}
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/[^0-9A-Za-z]/g, "").slice(0, 6))}
            onKeyDown={(e) => { if (e.key === "Enter") lookupByCode(code); }}
            placeholder="Mkt #"
            className="ml-auto h-8 w-24 font-mono text-center"
            data-testid="salesperson-code-input"
          />
          <Button
            variant="default"
            size="sm"
            onClick={() => lookupByCode(code)}
            className="h-8 text-xs bg-amber-600 hover:bg-amber-700"
            disabled={!code}
            data-testid="lookup-salesperson-btn"
          >
            Go
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPickerOpen(true)}
            className="h-8 text-xs"
            data-testid="open-salesperson-picker-btn"
          >
            <Search className="h-3.5 w-3.5 mr-1" /> Pick by name
          </Button>
        </>
      )}

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Pick a salesperson</DialogTitle>
          </DialogHeader>
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Search by name or marketing #"
            autoFocus
            data-testid="salesperson-picker-filter"
          />
          <div className="grid grid-cols-2 gap-2 max-h-[60vh] overflow-y-auto">
            {filtered.map((s) => (
              <button
                key={s.id}
                onClick={() => { onChange(s); setPickerOpen(false); setFilter(""); }}
                className="text-left p-3 border-2 rounded-lg bg-white hover:bg-amber-50 hover:border-amber-400 transition-all"
                data-testid={`pick-salesperson-${s.id}`}
              >
                <div className="font-bold text-slate-900">{s.name}</div>
                <div className="text-xs text-slate-500 font-mono">#{s.marketing_number || "—"}{s.position ? ` · ${s.position}` : ""}</div>
              </button>
            ))}
            {!filtered.length && <div className="col-span-2 text-center text-sm text-gray-500 py-6">No salespeople found. Go to Employees → tick "Salesperson" + give them a marketing #.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
